import { Component, computed, ElementRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { NbDialogService, NbIconLibraries, NbSidebarService, NbSidebarState } from '@nebular/theme';
import { AuthService } from '@services/auth.service';
import { UserDetailDto } from '@artemis-software/wr-api';
import { SessionService } from '@services/session.service';
import { WrMenuItem } from '@shared/components/menu/menu.component';
import { currentUser } from '@/utils/admin-utils';
import { CurrentUserService } from '@services/current-user.service';
import { appNavItems } from '@/app.nav';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';
import { defineCustomIcons } from '@/utils/custom-icons';
import {
  ChangeUserPasswordDialogComponent
} from '@components/dialog/change-user-password-dialog/change-user-password-dialog.component';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private readonly sidebarService = inject(NbSidebarService);
  private readonly authService = inject(AuthService);
  private readonly iconLibraries = inject(NbIconLibraries);
  private readonly sessionService = inject(SessionService);
  private readonly dialogService = inject(NbDialogService);
  private readonly elementRef = inject(ElementRef);
  private readonly router = inject(Router);
  readonly currentUserService = inject(CurrentUserService);

  readonly currentUser = currentUser();

  // No shell chrome on the forced password-change page: it must be unmissable and unclickable-around.
  readonly showShell = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => !event.urlAfterRedirects.startsWith('/force-password-change')),
    ),
    // router.url is still '/' at construction (initial navigation hasn't run), so read the real
    // browser path instead - otherwise the shell flashes on a hard reload of /force-password-change.
    { initialValue: !window.location.pathname.startsWith('/force-password-change') },
  );

  userMenuOpen = false;

  readonly tenantName = computed(() => {
    return this.currentUser()?.tenant.name ?? '';
  });

  readonly loading = loader();

  readonly sidebarState = signal<NbSidebarState>('compacted');

  readonly menuItems = asyncComputed(async () => {
    const currentUser = this.currentUser();
    if (!currentUser) {
      return currentUser;
    }
    return this.filterMenu([...appNavItems], currentUser).map(item => {
      let newChildren: WrMenuItem[] | undefined = undefined;
      if (item.children) {
        newChildren = this.filterMenu([...item.children], currentUser);
      }
      return {
        ...item,
        children: newChildren,
      };
    });
  }, undefined, this.loading);


  readonly dashboardSessionActive = this.authService.dashboardSessionActive;
  expanded = false;

  constructor() {
    this.iconLibraries.registerFontPack('fas', {
      packClass: 'fas',
      iconClassPrefix: 'fa',
    });
    this.iconLibraries.registerFontPack('far', {
      packClass: 'far',
      iconClassPrefix: 'fa',
    });
    this.iconLibraries.registerFontPack('fab', {
      packClass: 'fab',
      iconClassPrefix: 'fa',
    });
    this.iconLibraries.registerSvgPack('custom', defineCustomIcons());
    this.iconLibraries.setDefaultPack('far');

    this.authService.initJwt();
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: HTMLElement): void {
    if (!this.userMenuOpen) return;
    const wrapper: HTMLElement | null = this.elementRef.nativeElement.querySelector('.user-menu-wrapper');
    if (wrapper && !wrapper.contains(target)) {
      this.userMenuOpen = false;
    }
  }

  getUserProfile(user: UserDetailDto): string {
    const dummyUser = 'https://i.imgur.com/ujW6tKI.png';
    const profilePicture = user.userProfilePicture;
    if (profilePicture) {
      return profilePicture.presignedUrl ?? dummyUser;
    }
    return dummyUser;
  }

  toggleSidebar(): void {
    this.expanded = !this.expanded;
    this.sidebarService.toggle(true);
  }

  openChangePasswordDialog(): void {
    this.dialogService.open(ChangeUserPasswordDialogComponent);
  }

  ngOnInit() {
    this.sessionService.init();
  }

  filterMenu(items: WrMenuItem[], user: UserDetailDto | undefined | null): WrMenuItem[] {
    if (!user) {
      return [];
    }
    const tenantType = user.tenantType;
    return items.filter(item => {
      if (!item.tenantType) {
        if (item.permission) {
          return this.currentUserService.hasPrivilege(item.permission);
        }
        return true;
      }
      return item.tenantType === tenantType && (!item.permission || this.currentUserService.hasPrivilege(item.permission));
    });
  }
}
